import type { TypeProject } from '@/types/work/types.work.project';
import type { TypeTask, TypeTaskPriority, TypeTaskState } from '@/types/work/types.work.task';

export const STATE_LABEL: Record<TypeTaskState, string> = {
    idea: 'Idee',
    todo: 'Da fare',
    doing: 'In corso',
    review: 'In revisione',
    done: 'Fatto',
};

export const PRIORITY_LABEL: Record<TypeTaskPriority, string> = {
    low: 'Bassa',
    normal: 'Normale',
    high: 'Alta',
    urgent: 'Urgente',
};

export const PRIORITY_TONE: Record<TypeTaskPriority, 'gray' | 'info' | 'warning' | 'danger'> = {
    low: 'gray',
    normal: 'info',
    high: 'warning',
    urgent: 'danger',
};

const DAY = 86_400_000;

export const STILL_DAYS = 5;

export const ACTIVE_STATES: TypeTaskState[] = ['todo', 'doing', 'review'];

export const isActive = (task: TypeTask): boolean => ACTIVE_STATES.includes(task.state);

export const isLate = (task: TypeTask): boolean =>
    task.state !== 'done' && Boolean(task.dueAt) && Date.parse(task.dueAt as string) < Date.now();

export const daysStill = (task: TypeTask): number =>
    Math.floor((Date.now() - Date.parse(task.movedAt)) / DAY);

export const isStill = (task: TypeTask): boolean =>
    task.state === 'doing' && daysStill(task) >= STILL_DAYS;

export const WIP_LIMITS: Partial<Record<TypeTaskState, number>> = { doing: 3, review: 3 };

export function boardStats(all: TypeTask[], projects: TypeProject[]) {
    const tasks = all.filter((task) => !all.some((entry) => entry.parentId === task.id));
    const open = tasks.filter((task) => task.state !== 'done');
    const active = tasks.filter(isActive);
    const late = open.filter(isLate);
    const still = tasks.filter(isStill);

    const byId = new Map(projects.map((project) => [project.id, project]));
    const kindOf = (task: TypeTask) => byId.get(task.projectId)?.kind ?? 'internal';

    const forClients = active.filter((task) => kindOf(task) === 'client');
    const mine = active.filter((task) => kindOf(task) === 'internal');

    const lastOwnMove = mine.length
        ? Math.max(...mine.map((task) => Date.parse(task.movedAt)))
        : 0;

    return {
        total: tasks.length,
        open: open.length,
        active: active.length,
        doing: tasks.filter((task) => task.state === 'doing').length,
        todo: tasks.filter((task) => task.state === 'todo').length,
        done: tasks.filter((task) => task.state === 'done').length,
        idea: tasks.filter((task) => task.state === 'idea').length,

        late: late.length,
        worstLate: [...late].sort((a, b) => Date.parse(a.dueAt as string) - Date.parse(b.dueAt as string))[0] ?? null,
        dueThisWeek: open.filter((task) => task.dueAt
            && Date.parse(task.dueAt) >= Date.now()
            && Date.parse(task.dueAt) < Date.now() + 7 * DAY).length,

        still: still.length,
        worstStill: [...still].sort((a, b) => daysStill(b) - daysStill(a))[0] ?? null,

        forClients: forClients.length,
        mine: mine.length,
        clientShare: active.length ? (forClients.length / active.length) * 100 : 0,
        ownIdleDays: lastOwnMove ? Math.floor((Date.now() - lastOwnMove) / DAY) : 0,

        unassigned: active.filter((task) => !task.assigneeId).length,
        deliveries: all.filter((task) => !task.parentId).length,
        overWip: (Object.keys(WIP_LIMITS) as TypeTaskState[])
            .filter((state) => tasks.filter((task) => task.state === state).length > (WIP_LIMITS[state] ?? Infinity)).length,
        hours: active.reduce((sum, task) => sum + (task.estimateHours ?? 0), 0),
        projects: projects.filter((project) => project.status === 'active').length,
    };
}

export function stateMix(all: TypeTask[]): Record<TypeTaskState, number> {
    const tasks = all.filter((task) => !all.some((entry) => entry.parentId === task.id));
    const mix = { idea: 0, todo: 0, doing: 0, review: 0, done: 0 } as Record<TypeTaskState, number>;
    for (const task of tasks) mix[task.state] += 1;
    return mix;
}
